/* eslint-disable max-depth */
import { Injectable } from '@angular/core';
import { ApiCacheService } from '@shared/services/api-cache.service';
import { AuthService } from '@shared/services/auth.service';
import {
  CrewHistoryItem,
  CrewHistoryResponse,
} from './models/response/crew-history.response';
import { CrewHistoryRequest } from './models/request/crew-history.request';
import { environment } from '@environments/environment';
import { AlertService } from '@shared/services/alert/alert.service';
import { forkJoin, map, Observable, tap } from 'rxjs';
import { RequestState } from '@shared/models/request-state';
import { PilotNameResponse } from './models/response/pilot-name.response';
import { deepCopy } from '@shared/utilities/utilites';

@Injectable({
  providedIn: 'root',
})
export class CrewHistoryService {
  constructor(
    private authService: AuthService,
    private cacheService: ApiCacheService,
    private alertService: AlertService,
  ) {}

  getFlightsFlownWith(
    empNumber: string,
    page = 0,
  ): Observable<RequestState<CrewHistoryResponse>> {
    // If leading zero, get both leading zero and trimmed.
    if (empNumber.startsWith('0')) {
      const requests = {
        withZero: this.getPilotHistory(empNumber, page),
        withoutZero: this.getPilotHistory(parseInt(empNumber).toString(), page),
      };
      return forkJoin({
        withZero: requests.withZero,
        withoutZero: requests.withoutZero,
      }).pipe(
        map((responses) => {
          if (
            responses.withZero.response &&
            responses.withZero.response.items.length > 0
          ) {
            return responses.withZero;
          }

          if (
            responses.withoutZero.response &&
            responses.withoutZero.response.items.length > 0
          ) {
            return responses.withoutZero;
          }

          return responses.withZero;
        }),
      );
    } else {
      return this.getPilotHistory(empNumber, page);
    }
  }

  // Helper function for dealing with leading zero requests.
  private getPilotHistory(
    empNumber: string,
    page: number,
  ): Observable<RequestState<CrewHistoryResponse>> {
    const usersEmpNum = this.authService.empNumber;
    const url = `${environment.flightDataServiceUrl}/${usersEmpNum}`;

    const cacheId = `crew-history-${usersEmpNum}/${empNumber}-${page}`;
    const request: CrewHistoryRequest = {
      employeeNumber: empNumber,
      page,
    };

    return this.cacheService
      .request<CrewHistoryResponse>(cacheId, url, request)
      .pipe(
        tap((response) => {
          if (response.hasError) {
            this.showErrorAlert();
          }
        }),
        map((response) => {
          // Add page number to response.
          if (response.response) {
            response.response.page = page;
            const filteredLegs = this.filterLegsByEmpNum(
              response.response.items,
              empNumber,
            );
            response.response.items = this.filterNonOperatedSequences(
              filteredLegs,
              empNumber,
            );
          }
          return response;
        }),
      );
  }

  getPilotName(empNum: string): Observable<RequestState<PilotNameResponse>> {
    const url = `${environment.crewBasicUrl}`;
    const cacheId = `name-${empNum}`;
    const request = [empNum];
    return this.cacheService.request<PilotNameResponse>(cacheId, url, request);
  }

  /** Remove sequences where the only crew match is a deadhead. */
  private filterNonOperatedSequences(
    historyItems: CrewHistoryItem[],
    searchedEmpNum: string,
  ): CrewHistoryItem[] {
    const filteredSequences: CrewHistoryItem[] = [];

    for (const sequence of historyItems) {
      if (this.isFlownSequence(sequence, searchedEmpNum)) {
        filteredSequences.push(sequence);
      }
    }

    return filteredSequences;
  }

  private filterLegsByEmpNum(
    historyItems: CrewHistoryItem[],
    searchedEmpNum: string,
  ): CrewHistoryItem[] {
    const filteredItems = deepCopy(historyItems) as CrewHistoryItem[];

    for (const historyItem of filteredItems) {
      for (const dp of historyItem.dutyPeriodSummaries) {
        dp.legs = dp.legs.filter((leg) => {
          for (const pilot of leg.flightCrew) {
            if (searchedEmpNum === pilot.employeeNumber) {
              return true;
            }
          }
          return false;
        });
      }
    }

    return filteredItems;
  }

  /** Determine whether the user actually operated any of the flights within this sequence.
   *  Look for deadhead where searched employee number operated flight.
   */
  private isFlownSequence(
    sequence: CrewHistoryItem,
    searchedEmpNum: string,
  ): boolean {
    for (const dp of sequence.dutyPeriodSummaries) {
      for (const leg of dp.legs) {
        if (leg.isDeadhead) {
          for (const pilot of leg.flightCrew) {
            if (pilot.employeeNumber === searchedEmpNum) {
              return false;
            }
          }
        }
      }
    }

    return true;
  }

  private showErrorAlert(): void {
    this.alertService.setAlert({
      type: 'error',
      message: 'Unable to load pilot history information.',
    });
  }
}
